/**
 * CLI Product Board Management
 *
 * Superadmin CRUD for the Product board (cards.boardType='product_item'),
 * scoped to the org the CLI user is logged into. Backed by
 * `/superadmin/product-board/*`. Flag↔card linking re-surfaces the existing
 * `/superadmin/feature-flags/:name/product` endpoint for convenience.
 */

import process from 'node:process';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import { apiRequest, fetchList, getTargetOrg } from './cli-helpers.js';

type ProductItemCustomer = {
  id: string;
  name: string;
  orgId: string | null;
  accountCardId: string | null;
};

type ProductItem = {
  id: string;
  title: string;
  description: string;
  stage: string;
  priority: string | null;
  sortOrder: number;
  customers: ProductItemCustomer[];
  flagCount: number;
  flagDriven: boolean;
};

type ProductApiResponse = {
  ok: boolean;
  error?: string;
  item?: ProductItem;
  customer?: ProductItemCustomer;
};

type ProductCardFlag = {
  id: string;
  name: string;
  lifecycleStage: string;
  activeOrgGateCount: number;
};

type ProductCommentItem = {
  id: string;
  cardId: string;
  authorId: string;
  body: string;
  createdAt: string;
  updatedAt: string;
};

type ProductCommentsResponse = {
  ok: boolean;
  error?: string;
  data: ProductCommentItem[];
};

type ProductCommentCreateResponse = {
  ok: boolean;
  error?: string;
  data?: ProductCommentItem;
};

const STAGES = [
  'ideas',
  'prioritized',
  'in_development',
  'dogfooding',
  'beta',
  'on_hold',
  'ga',
  'internal',
  'deprecated',
];

function formatItem(item: ProductItem): string {
  const priority = item.priority ? ` ${item.priority}` : '';
  const flags = item.flagCount > 0 ? ` flags=${item.flagCount}${item.flagDriven ? '(driven)' : ''}` : '';
  const customers = item.customers.length > 0 ? ` customers=${item.customers.length}` : '';
  return `  ${item.id}  ${item.title}  (stage=${item.stage}${priority})${flags}${customers}`;
}

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const jsonOutput = hasFlag(argv, '--json');
  const params = new URLSearchParams();
  const search = getArgValue(argv, '--search');
  if (search) params.set('search', search);
  const priority = getArgValue(argv, '--priority');
  if (priority) params.set('priority', priority);
  if (hasFlag(argv, '--include-deprecated')) params.set('includeDeprecated', 'true');
  const query = params.toString();
  const path = `/superadmin/product-board/items${query ? `?${query}` : ''}`;
  const items = await fetchList<ProductItem>(apiUrl, token, path, 'items');

  if (jsonOutput) {
    console.log(JSON.stringify(items, null, 2));
    return;
  }
  if (items.length === 0) {
    console.log('No product items found.');
    return;
  }
  for (const item of items) console.log(formatItem(item));
}

async function handleCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const title = argv[0];
  if (!title || title.startsWith('--')) {
    console.error('Error: Missing title.');
    console.error(
      'Usage: canary product create <title> [--description <text>] [--stage <stage>] [--priority <p0-p3>]'
    );
    process.exit(1);
  }

  const description = getArgValue(argv, '--description');
  const stage = getArgValue(argv, '--stage');
  const priority = getArgValue(argv, '--priority');

  const result = await apiRequest<ProductApiResponse>(
    apiUrl,
    token,
    'POST',
    '/superadmin/product-board/items',
    {
      title,
      ...(description !== undefined ? { description } : {}),
      ...(stage !== undefined ? { stage } : {}),
      ...(priority !== undefined ? { priority } : {}),
    }
  );

  if (!result.ok || !result.item) {
    console.error(`Error: ${result.error ?? 'Failed to create product item'}`);
    process.exit(1);
  }
  console.log(`Created product item ${result.item.id}: ${result.item.title} (stage=${result.item.stage})`);
}

async function handleSet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  if (!id || id.startsWith('--')) {
    console.error('Error: Missing product item id.');
    console.error('Usage: canary product set <id> [--title <text>] [--description <text>] [--priority <p0-p3|null>]');
    process.exit(1);
  }

  const title = getArgValue(argv, '--title');
  const description = getArgValue(argv, '--description');
  const rawPriority = getArgValue(argv, '--priority');

  if (title === undefined && description === undefined && rawPriority === undefined) {
    console.error('Error: Provide at least one of --title, --description, --priority.');
    process.exit(1);
  }

  // `--priority null` clears the priority; otherwise pass the value through.
  const priority = rawPriority === undefined ? undefined : rawPriority === 'null' ? null : rawPriority;

  const result = await apiRequest<ProductApiResponse>(
    apiUrl,
    token,
    'PATCH',
    `/superadmin/product-board/items/${encodeURIComponent(id)}`,
    {
      ...(title !== undefined ? { title } : {}),
      ...(description !== undefined ? { description } : {}),
      ...(priority !== undefined ? { priority } : {}),
    }
  );

  if (!result.ok || !result.item) {
    console.error(`Error: ${result.error ?? 'Failed to update product item'}`);
    process.exit(1);
  }
  console.log(`Updated product item ${result.item.id}: ${result.item.title}`);
}

async function handleMove(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  const stage = getArgValue(argv, '--stage');
  if (!id || id.startsWith('--')) {
    console.error('Error: Missing product item id.');
    console.error('Usage: canary product move <id> --stage <stage> [--before <id>] [--after <id>]');
    process.exit(1);
  }
  if (!stage) {
    console.error(`Error: --stage is required (one of: ${STAGES.join(', ')}).`);
    process.exit(1);
  }

  const beforeItemId = getArgValue(argv, '--before') ?? null;
  const afterItemId = getArgValue(argv, '--after') ?? null;

  const result = await apiRequest<ProductApiResponse>(
    apiUrl,
    token,
    'POST',
    `/superadmin/product-board/items/${encodeURIComponent(id)}/move`,
    { stage, beforeItemId, afterItemId }
  );

  if (!result.ok || !result.item) {
    console.error(`Error: ${result.error ?? 'Failed to move product item'}`);
    process.exit(1);
  }
  console.log(`Moved product item ${result.item.id} to stage=${result.item.stage}`);
}

async function handleDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  if (!id || id.startsWith('--')) {
    console.error('Error: Missing product item id.');
    console.error('Usage: canary product delete <id>');
    process.exit(1);
  }

  const result = await apiRequest<ProductApiResponse>(
    apiUrl,
    token,
    'DELETE',
    `/superadmin/product-board/items/${encodeURIComponent(id)}`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  console.log(`Deleted product item ${id}`);
}

async function handleCustomer(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [action, id, ...rest] = argv;
  if (action === 'add') {
    if (!id) {
      console.error('Usage: canary product customer add <id> (--account-card <id> | --target-org <orgId>) [--name <text>]');
      process.exit(1);
    }
    const accountCardId = getArgValue(rest, '--account-card');
    const orgId = getTargetOrg(rest);
    const name = getArgValue(rest, '--name');
    if (!accountCardId && !orgId && !name) {
      console.error('Error: Provide --account-card, --target-org, or --name.');
      process.exit(1);
    }
    const result = await apiRequest<ProductApiResponse>(
      apiUrl,
      token,
      'POST',
      `/superadmin/product-board/items/${encodeURIComponent(id)}/customers`,
      {
        ...(accountCardId !== undefined ? { accountCardId } : {}),
        ...(orgId !== undefined ? { orgId } : {}),
        ...(name !== undefined ? { name } : {}),
      }
    );
    if (!result.ok || !result.customer) {
      console.error(`Error: ${result.error ?? 'Failed to add customer'}`);
      process.exit(1);
    }
    console.log(`Linked customer ${result.customer.name} to product item ${id}`);
    return;
  }

  if (action === 'remove') {
    const customerId = rest[0] ?? getArgValue(rest, '--customer');
    if (!id || !customerId) {
      console.error('Usage: canary product customer remove <id> <customerId>');
      process.exit(1);
    }
    const result = await apiRequest<ProductApiResponse>(
      apiUrl,
      token,
      'DELETE',
      `/superadmin/product-board/items/${encodeURIComponent(id)}/customers/${encodeURIComponent(customerId)}`
    );
    if (!result.ok) {
      console.error(`Error: ${result.error}`);
      process.exit(1);
    }
    console.log(`Removed customer ${customerId} from product item ${id}`);
    return;
  }

  console.error('Usage: canary product customer <add|remove> ...');
  process.exit(1);
}

async function handleLinkFlag(argv: string[], apiUrl: string, token: string): Promise<void> {
  const cardId = argv[0] && !argv[0].startsWith('--') ? argv[0] : undefined;
  const flagName = getArgValue(argv, '--flag');
  const clear = hasFlag(argv, '--clear');

  if (!flagName) {
    console.error('Error: --flag <name> is required.');
    console.error('Usage: canary product link-flag <cardId> --flag <name>   |   canary product link-flag --flag <name> --clear');
    process.exit(1);
  }
  if (clear === Boolean(cardId)) {
    console.error('Error: Provide either a <cardId> to link or --clear to unlink (not both/neither).');
    process.exit(1);
  }

  const result = await apiRequest<{ ok: boolean; error?: string; flag?: { productTitle?: string | null } }>(
    apiUrl,
    token,
    'POST',
    `/superadmin/feature-flags/${encodeURIComponent(flagName)}/product`,
    { productCardId: clear ? null : cardId }
  );

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  console.log(
    clear
      ? `Cleared product link for flag ${flagName}`
      : `Linked flag ${flagName} to product ${result.flag?.productTitle ?? cardId}`
  );
}

async function handleFlags(argv: string[], apiUrl: string, token: string): Promise<void> {
  const cardId = argv[0];
  const jsonOutput = hasFlag(argv, '--json');
  if (!cardId || cardId.startsWith('--')) {
    console.error('Usage: canary product flags <cardId>');
    process.exit(1);
  }
  const flags = await fetchList<ProductCardFlag>(
    apiUrl,
    token,
    `/superadmin/feature-flags/by-product-card/${encodeURIComponent(cardId)}`,
    'flags'
  );
  if (jsonOutput) {
    console.log(JSON.stringify(flags, null, 2));
    return;
  }
  if (flags.length === 0) {
    console.log('No feature flags linked to this product card.');
    return;
  }
  for (const flag of flags) {
    console.log(`  ${flag.name}  (lifecycle=${flag.lifecycleStage}, orgGates=${flag.activeOrgGateCount})`);
  }
}

async function handleComment(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  if (!id || id.startsWith('--')) {
    console.error('Error: Missing product item id.');
    console.error('Usage: canary product comment <id> <message>');
    console.error('       canary product comment <id> --body <message>');
    process.exit(1);
  }

  // Support both positional and --body flag
  let body = getArgValue(argv, '--body');
  if (!body) {
    const rest = argv.slice(1).filter((a) => !a.startsWith('--'));
    body = rest.join(' ').trim();
  }

  if (!body) {
    console.error('Error: Comment body is required.');
    console.error('Usage: canary product comment <id> "Your comment here"');
    process.exit(1);
  }

  // Product items are cards (boardType=product_item) — comments live on the
  // generic /cards/:id/comments endpoint, not the superadmin product-board API.
  const result = await apiRequest<ProductCommentCreateResponse>(
    apiUrl,
    token,
    'POST',
    `/cards/${encodeURIComponent(id)}/comments`,
    { body }
  );

  if (!result.ok || !result.data) {
    console.error(`Error: ${result.error ?? 'Failed to add comment'}`);
    process.exit(1);
  }
  console.log(`Comment added to product item ${id.slice(0, 8)}.`);
}

async function handleComments(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  if (!id || id.startsWith('--')) {
    console.error('Error: Missing product item id.');
    console.error('Usage: canary product comments <id>');
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, '--json');

  const result = await apiRequest<ProductCommentsResponse>(
    apiUrl,
    token,
    'GET',
    `/cards/${encodeURIComponent(id)}/comments`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Failed to list comments'}`);
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  if (result.data.length === 0) {
    console.log('No comments.');
    return;
  }

  console.log(`Comments (${result.data.length} total):\n`);
  for (const c of result.data) {
    const date = new Date(c.createdAt).toLocaleString();
    console.log(`  ${c.authorId.slice(0, 8)} · ${date}`);
    console.log(`  ${c.body}\n`);
  }
}

function printProductHelp(): void {
  console.log(
    [
      'Usage: canary product <sub-command> [options]',
      '',
      'Manage the Product board (cards.boardType=product_item) for the org you are logged into.',
      '',
      'Sub-commands:',
      '  list [--search <text>] [--priority p0,p1] [--include-deprecated] [--json]',
      '                                                List product items',
      '  create <title> [--description <text>] [--stage <stage>] [--priority <p0-p3>]',
      '                                                Create a product item',
      '  set <id> [--title <text>] [--description <text>] [--priority <p0-p3|null>]',
      '                                                Update a product item\'s fields',
      '  move <id> --stage <stage> [--before <id>] [--after <id>]',
      '                                                Move a card to another column (blocked when flag-driven)',
      '  delete <id>                                   Delete a product item',
      '  customer add <id> (--account-card <id> | --target-org <orgId>) [--name <text>]',
      '  customer remove <id> <customerId>             Link/unlink a customer (account card or org)',
      '  link-flag <cardId> --flag <name>              Link a feature flag to this product card',
      '  link-flag --flag <name> --clear               Clear a flag\'s product link',
      '  flags <cardId> [--json]                       List feature flags linked to a product card',
      '  comment <id> <message>                        Add a comment to a product item',
      '  comments <id> [--json]                        List comments on a product item',
      '',
      `Stages: ${STAGES.join(', ')}`,
      'Priorities: p0, p1, p2, p3',
      '',
      'Options:',
      '  --env <env>           Target environment (prod, dev, local)',
      '  --json                Output as JSON (list/flags only)',
      '  --api-url <url>       API URL override (takes precedence over --env)',
      '  --token <key>         API token override',
    ].join('\n')
  );
}

export async function runProduct(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printProductHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);

  switch (subCommand) {
    case 'list':
      await handleList(rest, apiUrl, token);
      break;
    case 'create':
      await handleCreate(rest, apiUrl, token);
      break;
    case 'set':
      await handleSet(rest, apiUrl, token);
      break;
    case 'move':
      await handleMove(rest, apiUrl, token);
      break;
    case 'delete':
      await handleDelete(rest, apiUrl, token);
      break;
    case 'customer':
      await handleCustomer(rest, apiUrl, token);
      break;
    case 'link-flag':
      await handleLinkFlag(rest, apiUrl, token);
      break;
    case 'flags':
      await handleFlags(rest, apiUrl, token);
      break;
    case 'comment':
      await handleComment(rest, apiUrl, token);
      break;
    case 'comments':
      await handleComments(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printProductHelp();
      process.exit(1);
  }
}
